import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { MsalProvider } from "@azure/msal-react";
import {
	getAccountForPortal,
	getApiTokenRequest,
	getPortalAuthority,
	loginRequest,
	msalInstance,
} from "@config/auth/msalConfig.ts";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@services/query/queryClient.ts";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/tiptap/styles.css";
import "@mantine/schedule/styles.css";
import "@mantine/charts/styles.css";
import "@gfazioli/mantine-qr-code/styles.css";
import "./index.css";
import "./styles/splitter.css";
import "./css/campus-map.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
	detectM365Environment,
	handleTeamsDeepLink,
} from "@helpers/environmentHelpers.ts";
import { useMsalAuthStore } from "@stores/useMsalAuthStore.ts";
import {
	PORTAL_MODE_CONFIG,
	usePortalModeStore,
} from "@stores/usePortalModeStore.ts";
import { getTeamsSsoToken } from "@config/auth/teamsAuth.ts";
import { handleTeamsAuthPopup, isTeamsAuthPopup } from "@config/auth/teamsAuthPopup.ts";
import { initializeBrowserTracing, reactErrorCallbacks, reportFrontendError } from "@services/telemetry/browserTracing";
import { frontendErrorContext } from "@services/telemetry/frontendErrorContext";
import { getPortalsApiBaseUrl, getPortalTelemetryHeaders } from "@services/api/portalApi";

try {
	initializeBrowserTracing({
		apiBaseUrl: getPortalsApiBaseUrl(),
		getHeaders: getPortalTelemetryHeaders,
		environment: __APP_ENVIRONMENT__,
		version: __APP_VERSION__,
		buildNumber: __APP_BUILD_NUMBER__,
		getErrorContext: frontendErrorContext,
		enabled: import.meta.env.VITE_BROWSER_TRACING_ENABLED !== "false",
	});
} catch {
	// Telemetry initialization must not prevent the portal from starting.
}

// App rendering waits for authentication bootstrap, but the portal mode is
// available synchronously from the hostname. Set the title before that wait.
document.title = PORTAL_MODE_CONFIG[usePortalModeStore.getState().hostMode].label;

async function bootstrap() {
	const root = createRoot(document.getElementById("root")!, reactErrorCallbacks);

	// Keep the parent portal closed before any auth or SSO bootstrap runs. This
	// applies to every path on the parent host, including browsers with an
	// existing Microsoft session or cached portal account.
	// if (usePortalModeStore.getState().hostMode === "parent") {
	// 	root.render(
	// 		<StrictMode>
	// 			<ParentPortalHoldingPage />
	// 		</StrictMode>,
	// 	);
	// 	return;
	// }

	await msalInstance.initialize();
	if (isTeamsAuthPopup()) {
		await handleTeamsAuthPopup();
		return;
	}
	const { environment } = await detectM365Environment();
	if (environment === "Teams") {
		await handleTeamsDeepLink();
		const teamsToken = await getTeamsSsoToken(true);
		if (teamsToken) {
			useMsalAuthStore.getState().setAuthed();
		}
	} else {
		let authenticationReady = false;
		try {
			const hostMode = usePortalModeStore.getState().hostMode;
			const redirectResponse = await msalInstance.handleRedirectPromise();
			if (
				redirectResponse?.account &&
				getAccountForPortal([redirectResponse.account], hostMode)
			) {
				msalInstance.setActiveAccount(redirectResponse.account);
			}

			let portalAccount = getAccountForPortal(
				msalInstance.getAllAccounts(),
				hostMode,
			);
			if (portalAccount) {
				msalInstance.setActiveAccount(portalAccount);
			}

			if (!portalAccount) {
				if (hostMode !== "parent") {
					// A hidden Microsoft sign-in iframe returning to the portal's
					// private IP can trigger local-network permission or time out.
					// Navigate directly; an existing Microsoft session still supplies SSO.
					await msalInstance.loginRedirect({
						...loginRequest,
						prompt: undefined,
					});
					return;
				}

				const ssoResponse = await msalInstance.ssoSilent({
					...getApiTokenRequest(hostMode),
					authority: getPortalAuthority(hostMode),
				});
				if (
					ssoResponse.account &&
					getAccountForPortal([ssoResponse.account], hostMode)
				) {
					msalInstance.setActiveAccount(ssoResponse.account);
					portalAccount = ssoResponse.account;
				}
			}

			if (portalAccount) {
				try {
					await msalInstance.acquireTokenSilent({
						...getApiTokenRequest(hostMode),
						authority: getPortalAuthority(hostMode),
						account: portalAccount,
					});
					authenticationReady = true;
				} catch (error) {
					if (error instanceof InteractionRequiredAuthError) {
						await msalInstance.loginRedirect({
							...getApiTokenRequest(hostMode),
							authority: getPortalAuthority(hostMode),
							account: portalAccount,
						});
						return;
					}
					throw error;
				}
			}
		} catch (e) {
			console.error(e);
			reportFrontendError(e, "AuthenticationBootstrap");
		}
		useMsalAuthStore
			.getState()
			.setFromAccountCount(authenticationReady ? 1 : 0);
	}

	root.render(
		<StrictMode>
			<QueryClientProvider client={queryClient}>
				<MsalProvider instance={msalInstance}>
					<App />
				</MsalProvider>
			</QueryClientProvider>
		</StrictMode>,
	);
}

void bootstrap();
